import React, { useEffect, useState } from 'react';
import { generateEventIntelligenceSummary } from '../../services/geminiService';
import LoadingSpinner from '../shared/LoadingSpinner';
import { TONES, ICONS } from '../shared/tones';
import { fmtCount } from '../shared/format';

/**
 * Pipeline-health verdict.
 * Inputs:
 *  - ECP score band (≥70 strong, 40–70 moderate, <40 quiet)
 *  - Playbook coverage % (≥75 covered, 40–75 partial, <40 thin)
 *  - Urgent events count (>0 within 14 days, no playbook, score ≥50)
 */
export function computeEventIntelligenceVerdict({ ecp, coverage, urgentCount = 0 } = {}) {
  const ecpScore = Number(ecp?.data?.ecp ?? 0);
  const eventsCount = Number(ecp?.data?.events ?? 0);
  const covPct = coverage?.data?.coverage_pct != null ? Number(coverage.data.coverage_pct) : null;
  const total = Number(coverage?.data?.total_opportunities ?? 0);

  if (eventsCount === 0 && total === 0) {
    return {
      tone: 'gray',
      icon: 'compass',
      label: 'Quiet window',
      headline: 'No scored events in this period yet.',
      subline: 'Widen the date range or wait for the next ingestion cycle to see opportunities.',
      cta: null,
    };
  }

  const cta = urgentCount > 0
    ? { label: `View ${urgentCount} urgent opportunit${urgentCount === 1 ? 'y' : 'ies'}`, href: '#opportunity-board' }
    : null;

  if (urgentCount > 0 && (covPct == null || covPct < 75)) {
    return {
      tone: 'red',
      icon: 'bolt',
      label: 'Act now',
      headline: `${urgentCount} urgent event${urgentCount === 1 ? '' : 's'} within 14 days have no playbook.`,
      subline: 'Generate playbooks today — lead time is short and these are your highest-leverage windows.',
      cta,
    };
  }

  if (ecpScore >= 70 && (covPct == null || covPct >= 75)) {
    return {
      tone: 'emerald',
      icon: 'trophy',
      label: 'Strong pipeline',
      headline: `Concentration score is ${fmtCount(Math.round(ecpScore))} across ${fmtCount(eventsCount)} events with ${covPct != null ? covPct.toFixed(0) + '%' : 'high'} playbook coverage.`,
      subline: 'Pipeline is healthy. Focus on activation quality — refine playbook copy and channel mix.',
      cta,
    };
  }

  if (ecpScore >= 40 && covPct != null && covPct < 40) {
    return {
      tone: 'amber',
      icon: 'alert',
      label: 'Coverage gap',
      headline: `${fmtCount(eventsCount)} scored events but only ${covPct.toFixed(0)}% have playbooks.`,
      subline: 'Demand signal is real — close the playbook gap before lead time runs out on the top events.',
      cta,
    };
  }

  if (ecpScore < 40) {
    return {
      tone: 'amber',
      icon: 'alert',
      label: 'Soft window',
      headline: `Event Concentration is ${fmtCount(Math.round(ecpScore))} — demand signal is weak this period.`,
      subline: 'Lower-leverage window. Focus on evergreen campaigns or extend the date range to find peaks.',
      cta,
    };
  }

  return {
    tone: 'gray',
    icon: 'compass',
    label: 'Steady',
    headline: `${fmtCount(eventsCount)} scored events, ECP ${fmtCount(Math.round(ecpScore))}${covPct != null ? `, coverage ${covPct.toFixed(0)}%` : ''}.`,
    subline: 'Pipeline is operating in line with expectations. Monitor playbook coverage as new events appear.',
    cta,
  };
}

export default function VerdictCard({ ecp, ami, velocity, coverage, diversity, urgentCount = 0, clientId, startDate, endDate }) {
  const [dismissed, setDismissed] = useState(false);
  const [aiText, setAiText] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const verdict = computeEventIntelligenceVerdict({ ecp, coverage, urgentCount });
  const tone = TONES[verdict.tone] ?? TONES.gray;

  // The service caches on `ei|clientId|startDate|endDate|verdictLabel`, so the
  // effect only needs those primitives plus `verdict.label` (which keys the
  // cache and is derived from the data objects). Depending on the `ecp`/`ami`/
  // `velocity`/`coverage`/`diversity` objects would re-fire the paid Gemini call
  // on every parent render that hands down new references. The objects are read
  // inside the closure; `hasData` is the single primitive gate. `verdict.headline`/
  // `.subline` are pure functions of the same inputs as `.label`, so they're
  // omitted. (Same fix as the CI VerdictCard.)
  const hasData = Number(ecp?.data?.events ?? 0) > 0 || Number(coverage?.data?.total_opportunities ?? 0) > 0;
  useEffect(() => {
    if (!clientId || !startDate || !endDate || !hasData) {
      setAiText(null);
      return;
    }
    let cancelled = false;
    setAiLoading(true);
    const fallbackText = `${verdict.headline} ${verdict.subline}`;
    generateEventIntelligenceSummary({
      clientId, startDate, endDate, ecp, ami, velocity, coverage, diversity, urgentCount,
      verdictLabel: verdict.label,
      fallbackText,
    })
      .then((res) => {
        if (!cancelled) {
          setAiText(res.success ? res.data : null);
          setAiLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAiText(null);
          setAiLoading(false);
        }
      });
    return () => { cancelled = true; };
  }, [clientId, startDate, endDate, verdict.label, hasData]); // eslint-disable-line

  if (dismissed) return null;

  return (
    <div
      role="region"
      aria-label="Event Intelligence top insight"
      className={`relative ${tone.bg} border ${tone.border} rounded-xl p-4`}
    >
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="absolute top-3 right-3 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
      <div className="flex items-start gap-3 pr-6">
        <svg className={`w-5 h-5 ${tone.icon} flex-shrink-0 mt-0.5`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {ICONS[verdict.icon] ?? ICONS.compass}
        </svg>
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">
            Top Insight — {verdict.label}
          </h3>
          {aiLoading && !aiText ? (
            <LoadingSpinner label="Analyzing event pipeline..." inline />
          ) : aiText ? (
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{aiText}</p>
          ) : (
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              <span className="font-medium text-gray-900 dark:text-gray-100">{verdict.headline}</span>{' '}
              {verdict.subline}
            </p>
          )}
          {verdict.cta && (
            <div className={`flex flex-wrap gap-x-4 gap-y-1.5 mt-3 pt-3 border-t ${tone.divider}`}>
              <a
                href={verdict.cta.href}
                className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              >
                <span className={`w-2 h-2 rounded-full ${tone.dot} flex-shrink-0`} />
                {verdict.cta.label} →
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
